import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { FormAnswers, Question } from '../types';
import { useFormPersistence } from '../hooks/useFormPersistence';
import { QUESTIONS } from '../data/questions';

interface FormContextValue {
  answers: FormAnswers;
  questions: Question[];
  currentStep: number;
  totalSteps: number;
  setAnswer: (id: string, value: string) => void;
  goNext: () => void;
  goBack: () => void;
  resetForm: () => void;
}

const FormContext = createContext<FormContextValue | null>(null);

export function FormProvider({ children }: { children: ReactNode }) {
  const { loadAnswers, loadStep, saveAnswers, saveStep, clearAll } = useFormPersistence();

  const [answers, setAnswers] = useState<FormAnswers>(() => loadAnswers());
  const [currentStep, setCurrentStep] = useState<number>(() => loadStep());
  // Correo traído por la cookie cross-fase (/whoami). Si existe, el paso del
  // correo se OCULTA del formulario: no se le pide a quien ya conocemos.
  const [identityEmail, setIdentityEmail] = useState<string | null>(null);

  // Lista activa de preguntas: sin el paso "email" cuando la cookie ya lo trae.
  const questions = useMemo(
    () => (identityEmail ? QUESTIONS.filter((q) => q.id !== 'email') : QUESTIONS),
    [identityEmail],
  );
  const totalSteps = questions.length;

  useEffect(() => {
    saveAnswers(answers);
  }, [answers]);

  useEffect(() => {
    saveStep(currentStep);
  }, [currentStep]);

  // Si la lista se acorta (se ocultó el correo) y el paso actual quedó fuera de
  // rango, lo reencuadra al último válido.
  useEffect(() => {
    if (currentStep > totalSteps - 1) setCurrentStep(totalSteps - 1);
  }, [totalSteps, currentStep]);

  // Precarga el correo del registro (cross-fase) desde la cookie hit_id, leída
  // server-side por la Pages Function /whoami. Guarda el correo Y oculta su paso.
  // Solo si el usuario aún no lo puso. Fail-open: si no hay identidad, no pasa nada.
  useEffect(() => {
    let vivo = true;
    fetch('/whoami')
      .then((r) => r.json())
      .then((d: { email?: string | null }) => {
        if (vivo && d && d.email) {
          const email = d.email as string;
          setIdentityEmail(email);
          setAnswers((prev) => (prev.email ? prev : { ...prev, email }));
        }
      })
      .catch(() => {});
    return () => {
      vivo = false;
    };
  }, []);

  const setAnswer = (id: string, value: string) => {
    setAnswers((prev) => ({ ...prev, [id]: value }));
  };

  const goNext = () => {
    setCurrentStep((prev) => Math.min(prev + 1, totalSteps - 1));
  };

  const goBack = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 0));
  };

  const resetForm = () => {
    clearAll();
    setAnswers({});
    setCurrentStep(0);
  };

  return (
    <FormContext.Provider
      value={{ answers, questions, currentStep, totalSteps, setAnswer, goNext, goBack, resetForm }}
    >
      {children}
    </FormContext.Provider>
  );
}

export function useFormContext() {
  const ctx = useContext(FormContext);
  if (!ctx) throw new Error('useFormContext must be used within FormProvider');
  return ctx;
}
